import * as React from 'react'

import ViewLayout from './layout/ViewLayout'
import ViewGroupedLayout from './layout/ViewGroupedLayout'
import FormLayout from './layout/FormLayout'
import SignatureLayout from './layout/SignatureLayout'
import FormSignatureLayout from './layout/FormSignatureLayout'
import SimpleAlert from '@components/modal/SimpleAlert'

import useReducer from '@store/useReducer'
import * as EdlStore from '@store/edl/edl'
import * as SiteStore from '@store/site/site'
import * as UserStore from '@store/user/user'
import * as ContactsStore from '@store/contacts/contacts'

import api from '@api/api'
import { EtatDesLieuxTypeEnum } from '@api/enum'
import Logger from '@utils/logger'

import useBreakpoint from '@utils/useBreakpoints'
import useNavigation from '@layout/useNavigation'
import useI18n from '@i18n/useI18n'

import { findManifestation, findTenant } from '@screens/edl/utils'

interface Props {
  id: string
}

const EDLSignatureScreen = ({ id }: Props) => {
  const i18n = useI18n()
  const navigation = useNavigation()
  const [, bp] = useBreakpoint()
  const isPhone = bp === 'phone'

  const forms = useReducer(EdlStore.store, (s) => s.forms)
  const form = useReducer(EdlStore.store, (s) => s.form)
  const manifestations = useReducer(SiteStore.store, (s) => s.manifestations)
  const tenants = useReducer(SiteStore.store, (s) => s.tenants)
  const site = useReducer(SiteStore.store, (s) => s.site)
  const user = useReducer(UserStore.store, (s) => s.user)

  const manif = React.useMemo(() => findManifestation(manifestations, form.manifestationId), [manifestations, form])
  const tenant = React.useMemo(() => findTenant(tenants, form.locatairePermanentId), [tenants, form])

  const name = !!manif ? manif.libelle : tenant ? tenant.tenant.nomLocatairePermanent : ''

  const [scene, setScene] = React.useState<SignatureScene>('GLOBAL')

  React.useEffect(() => {
    api.contacts
      .all()
      .then((res) => ContactsStore.actions.setContacts(res))
      .catch(Logger.error)
  }, [])

  React.useEffect(() => {
    const updateForms = forms.map((f) => (f.id === form.id && f.type === form.type ? form : f))
    EdlStore.actions.setForms(updateForms)
  }, [form])

  const onViparisSignature = ({ signature }: Partial<SignatureForm>) => {
    EdlStore.actions.setForm({ utilisateurInitiateurSignature: signature, usePrefSignature: false })
    return Promise.resolve()
  }

  const onOrganizerForm = ({ firstname, name, mail, signature }: Partial<SignatureForm>) => {
    const contact: ContactOrganisateurForm = {
      nom: name,
      prenom: firstname,
      email: mail,
      signatureFileUrl: signature,
    }

    EdlStore.actions.setForm({ contactOrganisateur: contact })
    return Promise.resolve()
  }

  const submit = () => {
    const body: EtatDesLieuxCreationRequest = {
      token: form.tokenIncident || '',
      commentaire: form.commentaire,
      incidentAccepteIds:
        form.type === EtatDesLieuxTypeEnum.ENTREE
          ? form.incidentAcceptIds
          : form.incidentOrganisateurIds.filter((id) => form.incidentAcceptIds.includes(id)),
      incidentRefuseIds:
        form.type === EtatDesLieuxTypeEnum.ENTREE
          ? []
          : form.incidentViparisIds.filter((id) => form.incidentAcceptIds.includes(id)),
      locatairePermanentId: form.locatairePermanentId,
      manifestationId: form.manifestationId,
      type: form.type,
      espaceIds: form.espaceIds,
      siteId: site?.id,
      signatureViparisBase64: form.usePrefSignature ? undefined : form.utilisateurInitiateurSignature,
      contactOrganisateur: !!form.contactOrganisateur
        ? {
            nom: form.contactOrganisateur.nom || '',
            prenom: form.contactOrganisateur.prenom || '',
            email: form.contactOrganisateur.email || '',
            signatureBase64: form.contactOrganisateur.signatureFileUrl || '',
          }
        : undefined,
      contacts: form.contacts,
    }

    return new Promise<void>((res, rej) => {
      SimpleAlert.open({
        title: i18n.t('screens.edlSignature.alert.sendTitle'),
        description: i18n.t('screens.edlSignature.alert.sendDescription'),
        closeCallback: res,
        buttons: [
          {
            label: i18n.t('screens.edl.spaces.back'),
            onClick: () => {
              SimpleAlert.close()
              res()
            },
            color: 'cancel',
          },
          {
            label: i18n.t('screens.edlSignature.alert.sendButton'),
            onClick: () => {
              if (form.rememberedSignature && !!form.utilisateurInitiateurSignature && user) {
                api.users
                  .signature({
                    filename: `signature_${user.nom}_${user.prenom}_${Date.now()}.png`,
                    signatureBase64: form.utilisateurInitiateurSignature,
                  })
                  .then(UserStore.actions.setUser)
                  .catch(Logger.error)
              }

              SimpleAlert.close()

              api.edl
                .send(body)
                .then((res) => {
                  EdlStore.actions.removeForm(form.id)
                  EdlStore.actions.resetForm()
                  navigation.push(`/edl/detail/${res.id}`)
                })
                .catch((err) => {
                  Logger.error(err)
                  SimpleAlert.open({
                    description: i18n.t('screens.edlSignature.alert.error'),
                    buttons: [{ label: i18n.t('common.ok'), onClick: () => SimpleAlert.close() }],
                  })
                })
                .finally(res)
            },
          },
        ],
      })
    })
  }

  if (scene === 'GLOBAL') {
    if (isPhone) {
      return (
        <ViewGroupedLayout
          title={i18n.t('screens.edl.title')}
          name={name}
          type={form.type}
          form={form}
          onSceneChange={setScene}
          onBack={navigation.goBack}
          onFinish={submit}
        />
      )
    } else {
      return (
        <ViewLayout
          title={i18n.t('screens.edl.title')}
          name={name}
          type={form.type}
          form={form}
          onSceneChange={setScene}
          onBack={navigation.goBack}
          onFinish={submit}
        />
      )
    }
  }

  if (scene === 'VIPARIS_SIGNATURE') {
    return (
      <SignatureLayout
        title={i18n.t('screens.edlSignature.viparisTitle')}
        name={name}
        type={form.type}
        form={{ signature: form.utilisateurInitiateurSignature }}
        onForm={onViparisSignature}
        onSceneChange={setScene}
        onBack={() => setScene('GLOBAL')}
      />
    )
  }

  if (scene === 'ORGANIZER_SIGNATURE') {
    if (isPhone) {
      return (
        <SignatureLayout
          title={i18n.t('screens.edlSignature.orgaTitle')}
          name={name}
          type={form.type}
          form={{
            name: form.contactOrganisateur?.nom,
            firstname: form.contactOrganisateur?.prenom,
            mail: form.contactOrganisateur?.email,
            signature: form.contactOrganisateur?.signatureFileUrl,
          }}
          onForm={onOrganizerForm}
          onSceneChange={setScene}
          onBack={() => setScene('FORM')}
        />
      )
    } else {
      return (
        <FormSignatureLayout
          title={i18n.t('screens.edlSignature.orgaTitle')}
          name={name}
          type={form.type}
          form={{
            name: form.contactOrganisateur?.nom,
            firstname: form.contactOrganisateur?.prenom,
            mail: form.contactOrganisateur?.email,
            signature: form.contactOrganisateur?.signatureFileUrl,
          }}
          onForm={onOrganizerForm}
          onSceneChange={setScene}
          onBack={() => setScene('GLOBAL')}
        />
      )
    }
  }

  if (scene === 'FORM') {
    return (
      <FormLayout
        title={i18n.t('screens.edlSignature.orgaTitle')}
        name={name}
        type={form.type}
        form={{
          name: form.contactOrganisateur?.nom,
          firstname: form.contactOrganisateur?.prenom,
          mail: form.contactOrganisateur?.email,
        }}
        onForm={onOrganizerForm}
        onSceneChange={setScene}
        onBack={() => setScene('GLOBAL')}
      />
    )
  }

  if (scene === 'FORM_SIGNATURE') {
    if (isPhone) {
      return (
        <FormLayout
          title={i18n.t('screens.edlSignature.orgaTitle')}
          name={name}
          type={form.type}
          form={{
            name: form.contactOrganisateur?.nom,
            firstname: form.contactOrganisateur?.prenom,
            mail: form.contactOrganisateur?.email,
          }}
          onForm={onOrganizerForm}
          onSceneChange={setScene}
          onBack={() => setScene('GLOBAL')}
        />
      )
    } else {
      return (
        <FormSignatureLayout
          title={i18n.t('screens.edlSignature.orgaTitle')}
          name={name}
          type={form.type}
          form={{
            name: form.contactOrganisateur?.nom,
            firstname: form.contactOrganisateur?.prenom,
            mail: form.contactOrganisateur?.email,
            signature: form.contactOrganisateur?.signatureFileUrl,
          }}
          onForm={onOrganizerForm}
          onSceneChange={setScene}
          onBack={() => setScene('GLOBAL')}
        />
      )
    }
  }
}

export default EDLSignatureScreen
